import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  PageHeader,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  Spinner,
  Modal,
  ModalFooter,
} from '@orientamos/ui'
import {
  Plus,
  FileCheck,
  Search,
  Trash2,
  Eye,
  Edit,
  Building2,
  CheckCircle,
  Send,
} from 'lucide-react'
import {
  listContracts,
  deleteContract,
  approveContract,
  sendContract,
  type ContractListItem,
  type ContractStatus,
} from '@lib/api'
import { formatDate } from '@orientamos/shared'
import { usePermissions } from '../hooks/usePermissions'

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'draft', label: 'Borrador' },
  { value: 'pending_review', label: 'En revision' },
  { value: 'approved', label: 'Aprobado' },
  { value: 'sent', label: 'Enviado' },
  { value: 'signed', label: 'Firmado' },
]

const statusBadgeVariants: Record<
  ContractStatus,
  'default' | 'warning' | 'success' | 'primary' | 'secondary'
> = {
  draft: 'default',
  pending_review: 'warning',
  approved: 'success',
  sent: 'primary',
  signed: 'secondary',
}

const statusLabels: Record<ContractStatus, string> = {
  draft: 'Borrador',
  pending_review: 'En revision',
  approved: 'Aprobado',
  sent: 'Enviado',
  signed: 'Firmado',
}

export default function ContractsPage() {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const [contracts, setContracts] = useState<ContractListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [contractToDelete, setContractToDelete] = useState<ContractListItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [approveModalOpen, setApproveModalOpen] = useState(false)
  const [contractToApprove, setContractToApprove] = useState<ContractListItem | null>(null)
  const [isApproving, setIsApproving] = useState(false)

  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [contractToSend, setContractToSend] = useState<ContractListItem | null>(null)
  const [isSending, setIsSending] = useState(false)

  const loadContracts = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await listContracts({
        page: pagination.page,
        limit: pagination.limit,
        search: search || undefined,
        status: (statusFilter as ContractStatus) || undefined,
      })

      if (result.success) {
        setContracts(result.data)
        setPagination(result.pagination)
      } else {
        setError('Error al cargar los contratos')
      }
    } catch {
      setError('Error de conexion')
    } finally {
      setIsLoading(false)
    }
  }, [pagination.page, pagination.limit, search, statusFilter])

  useEffect(() => {
    loadContracts()
  }, [loadContracts])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 1 }))
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const handleDelete = async () => {
    if (!contractToDelete) return

    setIsDeleting(true)
    try {
      const result = await deleteContract(contractToDelete.id)
      if (result.success) {
        setContracts((prev) => prev.filter((c) => c.id !== contractToDelete.id))
        setDeleteModalOpen(false)
        setContractToDelete(null)
      }
    } catch (err) {
      console.error('Error deleting contract:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleApprove = async () => {
    if (!contractToApprove) return

    setIsApproving(true)
    try {
      const result = await approveContract(contractToApprove.id)
      if (result.success) {
        // Update the contract in the list
        setContracts((prev) =>
          prev.map((c) =>
            c.id === contractToApprove.id ? { ...c, status: 'approved' as ContractStatus } : c
          )
        )
        setApproveModalOpen(false)
        setContractToApprove(null)
      }
    } catch (err) {
      console.error('Error approving contract:', err)
    } finally {
      setIsApproving(false)
    }
  }

  const handleSend = async () => {
    if (!contractToSend) return

    setIsSending(true)
    try {
      const result = await sendContract(contractToSend.id)
      if (result.success) {
        setContracts((prev) =>
          prev.map((c) =>
            c.id === contractToSend.id ? { ...c, status: 'sent' as ContractStatus } : c
          )
        )
        setSendModalOpen(false)
        setContractToSend(null)
      }
    } catch (err) {
      console.error('Error sending contract:', err)
    } finally {
      setIsSending(false)
    }
  }

  const openDeleteModal = (contract: ContractListItem) => {
    setContractToDelete(contract)
    setDeleteModalOpen(true)
  }

  const openApproveModal = (contract: ContractListItem) => {
    setContractToApprove(contract)
    setApproveModalOpen(true)
  }

  const openSendModal = (contract: ContractListItem) => {
    setContractToSend(contract)
    setSendModalOpen(true)
  }

  return (
    <div>
      <PageHeader
        title="Contratos"
        description="Visualiza y gestiona los contratos generados"
        breadcrumbs={[{ label: 'Inicio', href: '/' }, { label: 'Contratos' }]}
        actions={
          <Link to="/contracts/new">
            <Button>
              <Plus className="w-4 h-4 mr-1" />
              Nuevo Contrato
            </Button>
          </Link>
        }
      />

      {/* Filters */}
      <Card className="mb-6" padding="sm">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Buscar por numero de orden..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="w-44">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            />
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            icon={<FileCheck className="w-12 h-12" />}
            title="Error al cargar"
            description={error}
            action={<Button onClick={loadContracts}>Reintentar</Button>}
          />
        </Card>
      ) : contracts.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileCheck className="w-12 h-12" />}
            title="Sin contratos generados"
            description={
              search || statusFilter
                ? 'No se encontraron contratos con los filtros seleccionados'
                : 'Los contratos se generan a partir de ordenes de contrato. Crea un contrato seleccionando una orden y una plantilla.'
            }
            action={
              search || statusFilter ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('')
                    setStatusFilter('')
                  }}
                >
                  Limpiar filtros
                </Button>
              ) : (
                <Link to="/contracts/new">
                  <Button>
                    <Plus className="w-4 h-4 mr-1" />
                    Crear Contrato
                  </Button>
                </Link>
              )
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <Table>
            <TableHead>
              <TableRow hoverable={false}>
                <TableHeader>Orden</TableHeader>
                <TableHeader>Plantilla</TableHeader>
                <TableHeader>Inmueble</TableHeader>
                <TableHeader>Estado</TableHeader>
                <TableHeader>Fecha</TableHeader>
                <TableHeader className="w-40">Acciones</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {contracts.map((contract) => (
                <TableRow
                  key={contract.id}
                  onClick={() => navigate(`/contracts/${contract.id}`)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <div className="font-medium text-primary-600">{contract.orderNumber}</div>
                    <div className="text-xs text-neutral-500">v{contract.version}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{contract.templateName}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-neutral-400" />
                      <div className="text-sm">{contract.propertyAddress || 'Sin direccion'}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariants[contract.status]}>
                      {statusLabels[contract.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-neutral-500">
                      {formatDate(contract.createdAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/contracts/${contract.id}`)}
                        title="Ver"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {(contract.status === 'draft' || contract.status === 'pending_review') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/contracts/${contract.id}/edit`)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {(contract.status === 'draft' || contract.status === 'pending_review') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openApproveModal(contract)}
                          title="Aprobar"
                          className="text-success-600 hover:text-success-700 hover:bg-success-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      {contract.status === 'approved' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openSendModal(contract)}
                          title="Enviar"
                          className="text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      {(contract.status === 'draft' || can('contract:delete:any-status')) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteModal(contract)}
                          title="Eliminar"
                          className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200">
              <div className="text-sm text-neutral-500">
                Mostrando {(pagination.page - 1) * pagination.limit + 1} a{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                {pagination.total} contratos
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Eliminar Contrato"
        description="Esta accion no se puede deshacer"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas eliminar el contrato de la orden{' '}
          <span className="font-medium text-neutral-900">{contractToDelete?.orderNumber}</span>?
        </p>
        {contractToDelete && contractToDelete.status !== 'draft' && (
          <p className="mt-2 text-sm text-danger-600 font-medium">
            Advertencia: Este contrato no esta en borrador. Esta accion es irreversible.
          </p>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Approve Confirmation Modal */}
      <Modal
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        title="Aprobar Contrato"
        description="El contrato quedara listo para enviar"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas aprobar el contrato de la orden{' '}
          <span className="font-medium text-neutral-900">{contractToApprove?.orderNumber}</span>?
          Una vez aprobado no podra ser editado.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setApproveModalOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleApprove} loading={isApproving}>
            Aprobar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Send Confirmation Modal */}
      <Modal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        title="Marcar como Enviado"
        description="El contrato se marcara como enviado para firma"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas marcar como enviado el contrato de la orden{' '}
          <span className="font-medium text-neutral-900">{contractToSend?.orderNumber}</span>?
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setSendModalOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSend} loading={isSending}>
            Marcar Enviado
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
